/**
 * Retroactive duplicate scan across the whole library (#108).
 *
 * `detectDuplicate` answers "is this incoming record a duplicate of anything I already have?" —
 * one item against the library, which is right for `add`. Sweeping an existing library with it
 * would be O(n²): 6,000 references means 18 million normalized comparisons. This module reuses
 * the same normalization rules but groups by key instead, which is O(n).
 *
 * See spec/features/superseded.md.
 */

import type { CslItem } from "../../core/csl-json/types.js";
import { normalize } from "../search/normalizer.js";
import { getSupersededMark } from "../superseded/index.js";
import {
  BOOK_SECTION_TYPES,
  extractYear,
  normalizeAuthors,
  normalizeDoi,
  normalizeIsbnForComparison,
} from "./detector.js";

/** Identifier a scan can group by. */
export type DuplicateKeyType = "doi" | "pmid" | "isbn" | "arxiv" | "eric" | "scopus" | "title";

export const DUPLICATE_KEY_TYPES: readonly DuplicateKeyType[] = [
  "doi",
  "pmid",
  "isbn",
  "arxiv",
  "eric",
  "scopus",
  "title",
];

/**
 * Keys used when `--by` is not given.
 *
 * `title` is excluded: title + authors + year is the noisiest rule in `detector.ts`, and across a
 * whole library it produces false pairs (errata, translations, reprinted chapters) that the
 * identifier-based keys do not. It stays available opt-in.
 */
export const DEFAULT_DUPLICATE_KEY_TYPES: readonly DuplicateKeyType[] = [
  "doi",
  "pmid",
  "isbn",
  "arxiv",
  "eric",
  "scopus",
];

/** A set of references that match each other. */
export interface DuplicateGroup {
  /** Every key type that contributed to this group, in DUPLICATE_KEY_TYPES order */
  types: DuplicateKeyType[];
  /** The matched value per contributing type */
  keys: Partial<Record<DuplicateKeyType, string>>;
  /** Two or more references */
  items: CslItem[];
  /** True when the group is already fully linked by superseded pointers */
  resolved: boolean;
}

export interface ScanOptions {
  /** Key types to group by (default: DEFAULT_DUPLICATE_KEY_TYPES) */
  by?: readonly DuplicateKeyType[];
  /** Report groups already linked by superseded pointers (default: false) */
  includeResolved?: boolean;
}

/**
 * Grouping key for one reference under one key type, or null when it cannot participate.
 */
function keyFor(type: DuplicateKeyType, item: CslItem): string | null {
  switch (type) {
    case "doi":
      // DOI comparison is case-sensitive, matching detector.ts
      return item.DOI ? normalizeDoi(item.DOI) : null;
    case "pmid":
      return item.PMID ?? null;
    case "isbn":
      return isbnKey(item);
    case "arxiv":
      // Version suffix ignored, matching detector.ts
      return item.custom?.arxiv_id ? item.custom.arxiv_id.replace(/v\d+$/, "") : null;
    case "eric":
      return item.custom?.eric_id ?? null;
    case "scopus":
      return item.custom?.scopus_id ?? null;
    case "title":
      return titleAuthorYearKey(item);
  }
}

/**
 * ISBN key, with the chapter carve-out from `detector.ts`.
 *
 * Chapters are keyed by ISBN + title so that the twelve chapters of one edited volume do not
 * collapse into a single twelve-way "duplicate". `detector.ts` also pairs a chapter with the book
 * itself whenever either side is a `book` — harmless for a one-shot `add` check, but across a
 * library it would report every book alongside its own chapters, so the scan keeps them apart.
 */
function isbnKey(item: CslItem): string | null {
  if (!item.ISBN) {
    return null;
  }
  const isbn = normalizeIsbnForComparison(item.ISBN);
  if (!BOOK_SECTION_TYPES.includes(item.type)) {
    return isbn;
  }
  const title = item.title ? normalize(item.title) : null;
  return title ? `${isbn} ${title}` : null;
}

/** Title + authors + year key; all three must be present, matching detector.ts. */
function titleAuthorYearKey(item: CslItem): string | null {
  const title = item.title ? normalize(item.title) : null;
  const authors = normalizeAuthors(item);
  const year = extractYear(item);
  if (!title || !authors || !year) {
    return null;
  }
  return `${title} ${authors} ${year}`;
}

/**
 * Union-find over reference positions, so a pair matching on both DOI and PMID becomes one group
 * rather than two overlapping ones. Without it, `--fix` would try to mark the same record twice.
 */
class DisjointSet {
  private readonly parent: number[];

  constructor(size: number) {
    this.parent = Array.from({ length: size }, (_, i) => i);
  }

  find(a: number): number {
    let root = a;
    while (this.parent[root] !== root) {
      root = this.parent[root] as number;
    }
    // Path compression keeps repeated lookups flat on large libraries
    let node = a;
    while (this.parent[node] !== root) {
      const next = this.parent[node] as number;
      this.parent[node] = root;
      node = next;
    }
    return root;
  }

  union(a: number, b: number): void {
    const rootA = this.find(a);
    const rootB = this.find(b);
    if (rootA !== rootB) {
      this.parent[rootB] = rootA;
    }
  }
}

/** Matched-key bookkeeping while groups are being assembled. */
interface KeyHit {
  type: DuplicateKeyType;
  key: string;
  positions: number[];
}

function collectKeyHits(items: CslItem[], types: readonly DuplicateKeyType[]): KeyHit[] {
  const hits: KeyHit[] = [];

  for (const type of types) {
    const buckets = new Map<string, number[]>();
    for (let i = 0; i < items.length; i++) {
      const key = keyFor(type, items[i] as CslItem);
      if (key === null) continue;
      const bucket = buckets.get(key);
      if (bucket) {
        bucket.push(i);
      } else {
        buckets.set(key, [i]);
      }
    }
    for (const [key, positions] of buckets) {
      if (positions.length >= 2) {
        hits.push({ type, key, positions });
      }
    }
  }

  return hits;
}

/**
 * Whether every member but one already points at another member of the same group.
 *
 * Without this, the pairs fixed on one run would be reported again on the next one forever.
 */
function isResolved(members: CslItem[]): boolean {
  const memberUuids = new Set(
    members.map((item) => item.custom?.uuid).filter((uuid): uuid is string => Boolean(uuid))
  );
  const unresolved = members.filter((item) => {
    const mark = getSupersededMark(item);
    return !mark || !memberUuids.has(mark.supersededBy);
  });
  return unresolved.length <= 1;
}

/**
 * Find every set of references in `items` that match each other.
 *
 * @param items - The whole library
 * @param options - Key types to use, and whether to report already-linked groups
 * @returns Groups of two or more references, largest first
 */
/** Merge every key hit into connected components. */
function unionHits(hits: KeyHit[], size: number): DisjointSet {
  const sets = new DisjointSet(size);
  for (const hit of hits) {
    const [first, ...rest] = hit.positions;
    for (const position of rest) {
      sets.union(first as number, position);
    }
  }
  return sets;
}

/** Collect the distinct positions belonging to each component root. */
function groupPositionsByRoot(hits: KeyHit[], sets: DisjointSet): Map<number, number[]> {
  const membersByRoot = new Map<number, Set<number>>();
  for (const hit of hits) {
    for (const position of hit.positions) {
      const root = sets.find(position);
      const existing = membersByRoot.get(root);
      if (existing) {
        existing.add(position);
      } else {
        membersByRoot.set(root, new Set([position]));
      }
    }
  }
  return new Map([...membersByRoot].map(([root, positions]) => [root, [...positions]]));
}

/** The key types and matched values that put one component together. */
function describeComponent(
  hits: KeyHit[],
  sets: DisjointSet,
  root: number
): { types: DuplicateKeyType[]; keys: Partial<Record<DuplicateKeyType, string>> } {
  const keys: Partial<Record<DuplicateKeyType, string>> = {};
  const types: DuplicateKeyType[] = [];
  for (const type of DUPLICATE_KEY_TYPES) {
    const hit = hits.find((h) => h.type === type && sets.find(h.positions[0] as number) === root);
    if (hit) {
      keys[type] = hit.key;
      types.push(type);
    }
  }
  return { types, keys };
}

export function scanDuplicates(items: CslItem[], options: ScanOptions = {}): DuplicateGroup[] {
  const types = options.by ?? DEFAULT_DUPLICATE_KEY_TYPES;
  const hits = collectKeyHits(items, types);
  const sets = unionHits(hits, items.length);

  const groups: DuplicateGroup[] = [];
  for (const [root, positions] of groupPositionsByRoot(hits, sets)) {
    if (positions.length < 2) continue;

    const members = positions.map((p) => items[p] as CslItem);
    const resolved = isResolved(members);
    if (resolved && !options.includeResolved) continue;

    const { types: typesInGroup, keys } = describeComponent(hits, sets, root);
    groups.push({ types: typesInGroup, keys, items: members, resolved });
  }

  // Largest first: a five-way pile-up deserves attention before a plain pair
  return groups.sort((a, b) => b.items.length - a.items.length);
}

/** Fields whose presence suggests a more complete record. */
const COMPLETENESS_FIELDS = [
  "DOI",
  "PMID",
  "PMCID",
  "page",
  "volume",
  "issue",
  "container-title",
  "abstract",
  "publisher",
] as const;

function completenessScore(item: CslItem): number {
  let score = COMPLETENESS_FIELDS.reduce((total, field) => total + (item[field] ? 1 : 0), 0);
  if (item.author && item.author.length > 0) score += 1;
  if (item.custom?.attachments?.files?.length) score += 1;
  return score;
}

/**
 * Suggest which member of a group to keep.
 *
 * Completeness first, then the later publication year. That ordering is what separates an
 * online-first record from its version of record: the VoR is the one that gained volume, issue
 * and page numbers. This is a suggestion — `--fix` presents it as the pre-selected choice, never
 * applies it unattended.
 */
export function suggestKeeper(items: CslItem[]): CslItem {
  const ranked = [...items].sort((a, b) => {
    const bySuperseded =
      Number(Boolean(getSupersededMark(a))) - Number(Boolean(getSupersededMark(b)));
    if (bySuperseded !== 0) return bySuperseded;

    const byCompleteness = completenessScore(b) - completenessScore(a);
    if (byCompleteness !== 0) return byCompleteness;

    const yearA = Number(extractYear(a) ?? 0);
    const yearB = Number(extractYear(b) ?? 0);
    if (yearA !== yearB) return yearB - yearA;

    // Stable last resort so repeated runs suggest the same record
    return (a.custom?.created_at ?? "").localeCompare(b.custom?.created_at ?? "");
  });

  return ranked[0] as CslItem;
}
